/* eslint-disable jsdoc/require-jsdoc -- 私有状态操作由批次摘要及页面测试约束。 */
import { Alert, Button, Drawer, Input, Select, Space, Table, Tag } from "@arco-design/web-react";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  analyzeExternalStayImport,
  executeExternalStayImport, loadExternalStayImport, previewExternalStayImport,
  resolveExternalStayImport, type ExternalStayBatch, type ExternalStayPlanRow,
} from "../service/stayExternalImportService";

const labels = { new: "拟新增", update: "拟更新", review: "待确认" };
const fields: Record<string, string> = {
  hotel_name: "酒店名称", province: "省份", city: "城市", country: "国家", district: "区县",
  address: "地址", stay_level: "住宿档次", public_phones: "公开电话", room_count: "房间总数",
  opening_year: "开业年份", renovation_year: "装修年份", platform_rating: "评分", lng: "经度", lat: "纬度",
};

/** 酒店外部导入入口：文件变化即作废预览，执行使用服务器批次。 */
export function StayExternalImportDrawer({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const [file, setFile] = useState<File>();
  const [decisions, setDecisions] = useState<File>();
  const [date, setDate] = useState(new Date().toLocaleDateString("sv-SE"));
  const [batch, setBatch] = useState<ExternalStayBatch>();
  const [restoreId, setRestoreId] = useState("");
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [review, setReview] = useState<ExternalStayPlanRow>();
  const [action, setAction] = useState<"new" | "update" | "review">("review");
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");

  async function run(task: () => Promise<ExternalStayBatch>) {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await task();
      setBatch(result);
      setReview(undefined);
      setRestoreId(result.batch_id);
      if (result.status === "completed") {
        await client.invalidateQueries({ queryKey: ["stay-resources"] });
        await client.invalidateQueries({ queryKey: ["stay-resource"] });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "操作失败，可通过批次编号重新读取结果");
    } finally {
      running.current = false;
      setBusy(false);
    }
  }

  const writable = batch?.status === "preview";
  const executable = batch?.rows.filter((r) => r.action !== "review").length ?? 0;
  const counts = (key: string) => batch?.rows.filter((r) => r.action === key).length ?? 0;

  return <Drawer visible width="90%" title="导入外部酒店" onCancel={() => !busy && onClose()}
    maskClosable={false} footer={
      <Space>
        <span>{batch?.status === "completed" ? "批次已执行，待确认行未入库" : `本次可执行 ${executable} 家`}</span>
        <Button type="primary" loading={busy} disabled={!writable || !executable}
          onClick={() => batch && void run(() => executeExternalStayImport(batch.batch_id, batch.token))}>
          执行已确认酒店
        </Button>
      </Space>
    }>
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Alert content="已绑定携程 ID 优先复用；有效非空字段覆盖主档，空值保留。待确认行不会入库。"/>
      <Space wrap>
        <label>酒店 Excel <input aria-label="酒店 Excel" type="file" accept=".xlsx,.xls,.csv" disabled={busy}
          onChange={(e) => { setFile(e.target.files?.[0]); setBatch(undefined); setReview(undefined); }} /></label>
        <label>已审核判断包（可选） <input aria-label="已审核判断包" type="file" accept=".json" disabled={busy}
          onChange={(e) => { setDecisions(e.target.files?.[0]); setBatch(undefined); setReview(undefined); }} /></label>
        <label>数据所属日期 <input aria-label="数据所属日期" type="date" value={date} disabled={busy}
          onChange={(e) => { setDate(e.target.value); setBatch(undefined); setReview(undefined); }} /></label>
        <Button disabled={!file || !date || busy} onClick={() => file && void run(() => previewExternalStayImport(file, date, decisions))}>生成预览</Button>
      </Space>
      <Space>
        <Input aria-label="导入批次编号" placeholder="粘贴批次编号，恢复预览或核对执行结果" value={restoreId}
          disabled={busy} onChange={setRestoreId} style={{ width: 390 }} />
        <Button disabled={!restoreId || busy} onClick={() => void run(() => loadExternalStayImport(restoreId))}>读取批次</Button>
      </Space>
      {error && <Alert type="error" content={error}/>}
      {batch && <>
        <div>文件：{batch.file_name} / 数据日期：{batch.data_date} / 批次：{batch.batch_id}</div>
        <Space>
          <Tag color="green">新增 {counts("new")}</Tag><Tag color="blue">更新 {counts("update")}</Tag><Tag color="orange">待确认 {counts("review")}</Tag>
          <Button disabled={busy || !writable || !batch.rows.some((r) => r.action === "review" && r.reason === "无确定身份，需要模型或人工判断")}
            onClick={() => void run(() => analyzeExternalStayImport(batch.batch_id, batch.token))}>
            模型判断未识别酒店（每次最多 20 家）
          </Button>
          <Select aria-label="导入结果筛选" value={filter} onChange={setFilter} style={{ width: 150 }}
            options={[{ label: "全部", value: "all" }, ...Object.entries(labels).map(([value, label]) => ({ value, label }))]} />
        </Space>
        <Table<ExternalStayPlanRow> rowKey="source_row" size="small" pagination={{ pageSize: 30 }}
          data={batch.rows.filter((r) => filter === "all" || r.action === filter)}
          columns={[
            { title: "行", dataIndex: "source_row", width: 60 },
            { title: "酒店", dataIndex: "hotel_name", width: 250 },
            { title: "携程 ID", dataIndex: "source_hotel_id", width: 120 },
            { title: "处理", render: (_, r) => labels[r.action], width: 90 },
            { title: "依据", dataIndex: "reason" },
            { title: "目标酒店 ID", dataIndex: "target_id", width: 200 },
            { title: "操作", width: 100, render: (_, r) => writable && r.action === "review" &&
              <Button size="mini" disabled={busy} onClick={() => { setReview(r); setAction("review"); setTarget(""); setReason(""); }}>确认身份</Button> },
          ]}
          expandedRowRender={(r) => <div>
            <details><summary>原始来源资料</summary>
              {Object.entries(r.source_fields ?? {}).map(([key, value]) => <div key={key}>{key}：{value}</div>)}
            </details>
            {Object.entries(r.patch).length
            ? Object.entries(r.patch).map(([key, value]) => <div key={key}>{fields[key] ?? key}：{JSON.stringify(r.before[key] ?? null)} → {JSON.stringify(value)}</div>)
            : "无可写字段变更"}</div>}
        />
      </>}
      {review && writable && <Space direction="vertical" style={{ width: "100%" }}>
        <strong>确认第 {review.source_row} 行：{review.hotel_name}</strong>
        <Space>
          <Select aria-label="身份判断" value={action} onChange={setAction} style={{ width: 140 }}
            options={[{ value: "review", label: "继续待确认" }, { value: "new", label: "确认新增" }, { value: "update", label: "匹配已有酒店" }]} />
          {action === "update" && <Input aria-label="目标酒店 ID" placeholder="已有房资源 SPU ID" value={target} onChange={setTarget}/>}
          <Input aria-label="判断依据" placeholder="请填写判断依据" value={reason} onChange={setReason} style={{ width: 420 }}/>
          <Button disabled={busy || !reason.trim() || (action === "update" && !target.trim())}
            onClick={() => batch && void run(() => resolveExternalStayImport(batch.batch_id, {
              token: batch.token, source_row: review.source_row, action, target_id: target || null, reason,
            }))}>保存判断并更新预览</Button>
        </Space>
      </Space>}
    </Space>
  </Drawer>;
}
